import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useSync } from "@/context/SyncContext";
import { AppShell } from "@/components/AppShell";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { LogOut, Mail, User as UserIcon, ShieldCheck, Trash2, Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { ReportPersonalization } from "@/components/ReportPersonalization";
import { BillingSection } from "@/components/BillingSection";
import { CheckupReminderToggle } from "@/components/CheckupReminderToggle";
import { LocationRecordToggle } from "@/components/LocationRecordToggle";
import { EvidenceTakeout } from "@/components/EvidenceTakeout";
import { SiteFooter } from "@/components/SiteFooter";

export default function Settings() {
  const { user, logout } = useAuth();
  const { requestLogout } = useSync();
  const navigate = useNavigate();
  const initials = (user?.name || "U").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [errorState, setErrorState] = useState(null); // null | "generic" | "support"

  const doLogout = () => requestLogout(() => navigate("/"));

  // Deep-link support: /settings#report-styling scrolls to that section on load.
  useEffect(() => {
    const hash = window.location.hash?.slice(1);
    if (!hash) return;
    const t = setTimeout(() => {
      document.getElementById(hash)?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 200);
    return () => clearTimeout(t);
  }, []);

  const deleteAccount = async () => {
    setDeleting(true);
    setErrorState(null);
    try {
      await api.delete("/account");
      await logout();
      navigate("/");
      toast.success("Your data has been deleted.");
    } catch (e) {
      const status = e?.response?.status;
      setErrorState(status === 500 ? "support" : "generic");
      setDeleting(false);
    }
  };

  const closeDialog = () => {
    if (deleting) return;
    setDialogOpen(false);
    setConfirmText("");
    setErrorState(null);
  };

  return (
    <AppShell>
      <div className="max-w-5xl mx-auto">
      <h1 className="font-heading text-3xl font-bold tracking-tight text-navy">Settings</h1>
      <p className="mt-1 text-[#475569]">Manage your account.</p>

      {/* Masonry-style two-column packing on desktop (single column on mobile/tablet).
          Cards vary in height, so a rigid grid leaves awkward vertical gaps; CSS multi-column
          balances the two columns and packs cards tightly with no cross-row gaps. */}
      <div className="mt-6 columns-1 lg:columns-2 gap-6 [column-fill:_balance]">
        <div className="mb-6 break-inside-avoid rounded-2xl border border-[#E2E8F0] bg-white p-6">
          <div className="flex items-center gap-4">
            <Avatar className="h-16 w-16 shrink-0">
              <AvatarImage src={user?.picture} />
              <AvatarFallback className="bg-navy text-white text-xl font-semibold">{initials}</AvatarFallback>
            </Avatar>
            <div className="min-w-0">
              <h2 className="font-heading text-xl font-semibold text-navy truncate">{user?.name}</h2>
              <p className="text-[#475569] break-words">{user?.email}</p>
            </div>
          </div>

          <div className="mt-6 space-y-3">
            <Row icon={UserIcon} label="Name" value={user?.name} />
            <Row icon={Mail} label="Email" value={user?.email} />
            <Row icon={ShieldCheck} label="Sign-in method" value={user?.auth_provider === "google" ? "Google" : "Email & password"} />
          </div>

          <Button data-testid="settings-logout-btn" onClick={doLogout} variant="outline" className="mt-6 rounded-xl border-[#FECACA] text-[#B91C1C] hover:bg-[#FEE2E2]">
            <LogOut size={16} className="mr-2" /> Log out
          </Button>
        </div>

        {/* Plan & billing */}
        <div className="mb-6 break-inside-avoid">
          <BillingSection />
        </div>

        {/* Email reminders (Plus only — renders null otherwise) */}
        <div className="mb-6 break-inside-avoid empty:mb-0">
          <CheckupReminderToggle />
        </div>

        <div className="mb-6 break-inside-avoid">
          <LocationRecordToggle />
        </div>

        {/* Evidence Takeout — free full-account export */}
        <div className="mb-6 break-inside-avoid">
          <EvidenceTakeout scope="account" />
        </div>

        {/* Evidence PDF personalization (Premium) */}
        <div id="report-styling" className="mb-6 break-inside-avoid scroll-mt-24">
          <ReportPersonalization />
        </div>
      </div>

      {/* Danger zone — full width below the columns */}
      <div className="rounded-2xl border border-[#FECACA] bg-[#FEF2F2] p-6" data-testid="danger-zone">
        <h2 className="font-heading text-lg font-semibold text-[#B91C1C] flex items-center gap-2"><AlertTriangle size={18} /> Delete account</h2>
        <p className="mt-2 text-sm text-[#7F1D1D]">
          Permanently delete your account and <strong>all</strong> of your data — properties, photos, reports, comparisons, letters, and share links. This cannot be undone.
        </p>
        <Button data-testid="delete-account-open-btn" onClick={() => setDialogOpen(true)} className="mt-4 rounded-xl bg-[#B91C1C] hover:bg-[#991B1B] text-white">
          <Trash2 size={16} className="mr-2" /> Delete my account &amp; data
        </Button>
      </div>
      </div>

      <Dialog open={dialogOpen} onOpenChange={(o) => (o ? setDialogOpen(true) : closeDialog())}>
        <DialogContent data-testid="delete-account-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#B91C1C]">Delete your account?</DialogTitle>
            <DialogDescription>
              This permanently erases your account and everything you've documented. There is no way to recover it.
              Type <strong>DELETE</strong> below to confirm.
            </DialogDescription>
          </DialogHeader>

          <Input
            data-testid="delete-confirm-input"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder="Type DELETE"
            disabled={deleting}
            className="rounded-xl"
          />

          {errorState === "support" && (
            <p data-testid="delete-error-support" className="text-sm text-[#B91C1C] flex items-start gap-2">
              <AlertTriangle size={16} className="mt-0.5 shrink-0" />
              We couldn't fully delete your account. Please contact support so we can complete it — your data was not partially removed.
            </p>
          )}
          {errorState === "generic" && (
            <p data-testid="delete-error-generic" className="text-sm text-[#B91C1C] flex items-start gap-2">
              <AlertTriangle size={16} className="mt-0.5 shrink-0" />
              Something went wrong. Please try again.
            </p>
          )}

          <DialogFooter>
            <Button data-testid="delete-cancel-btn" variant="outline" onClick={closeDialog} disabled={deleting} className="rounded-xl">Cancel</Button>
            <Button
              data-testid="delete-confirm-btn"
              onClick={deleteAccount}
              disabled={confirmText !== "DELETE" || deleting}
              className="rounded-xl bg-[#B91C1C] hover:bg-[#991B1B] text-white disabled:opacity-50"
            >
              {deleting ? <><Loader2 size={16} className="mr-2 animate-spin" /> Deleting…</> : <><Trash2 size={16} className="mr-2" /> Permanently delete</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <div className="mt-8 -mx-4 sm:-mx-6">
        <SiteFooter className="bg-transparent border-t-0" />
      </div>
    </AppShell>
  );
}

function Row({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-3 rounded-xl bg-[#FAF7F2] border border-[#E2E8F0] px-4 py-3">
      <Icon size={18} className="text-[#94A3B8]" />
      <div>
        <p className="text-xs font-semibold uppercase tracking-wider text-[#94A3B8]">{label}</p>
        <p className="text-sm font-medium text-[#0F172A]">{value}</p>
      </div>
    </div>
  );
}
